/**
 * FetchStationsUseCase Implementation
 *
 * Orchestrates station fetching through the StationGateway.
 * Use-cases coordinate entities and gateways but don't implement business rules.
 *
 * Clean Architecture:
 * - usecase-orchestrates-not-implements: Delegates to gateway
 * - usecase-single-responsibility: Only handles fetch orchestration
 * - usecase-explicit-dependencies: Gateway injected via constructor
 */

import type {
  FetchStationsUseCase,
  FetchStationsInput,
  FetchStationsOutput,
} from '@/domain/use-cases';
import type { StationGateway } from '@/domain/gateways';
import { Logger } from '@/app/utils/logger';

const LOG_TAG = 'fetch-stations-usecase';

/**
 * Implementation of FetchStationsUseCase.
 *
 * Fetches stations through the injected gateway and returns
 * the result in a use-case-specific output format.
 */
export class FetchStationsUseCaseImpl implements FetchStationsUseCase {
  constructor(private readonly stationGateway: StationGateway) {}

  /**
   * Execute the use-case to fetch stations.
   *
   * @param input - Optional input with forceRefresh flag
   * @returns Promise with stations array, source, and staleness info
   */
  async execute(input?: FetchStationsInput): Promise<FetchStationsOutput> {
    const forceRefresh = input?.forceRefresh ?? false;

    try {
      Logger.info(`Executing FetchStationsUseCase (forceRefresh=${forceRefresh})`, LOG_TAG);

      const result = await this.stationGateway.fetchStations({
        forceRefresh,
      });

      Logger.info(
        `Stations fetched: ${result.stations.length} stations, source=${result.source}, stale=${result.isStale}`,
        LOG_TAG
      );

      return {
        stations: result.stations,
        source: result.source,
        isStale: result.isStale,
      };
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      Logger.error(`FetchStationsUseCase failed: ${message}`, LOG_TAG);
      throw error;
    }
  }
}

/**
 * Factory function to create FetchStationsUseCaseImpl with gateway.
 *
 * @param stationGateway - Gateway for station operations
 * @returns Configured FetchStationsUseCase instance
 */
export function createFetchStationsUseCase(
  stationGateway: StationGateway
): FetchStationsUseCase {
  return new FetchStationsUseCaseImpl(stationGateway);
}
